import { Link } from "react-router-dom";
import { Tag, Ticket, ArrowUpRight } from "lucide-react";

/**
 * iter237 — Sale + Offers tile pair for the customer homepage.
 *
 * Two large editorial tiles sit side-by-side on tablet+ and stack on
 * mobile. Designed to give the storefront two high-conversion entry
 * points (limited-time pricing + active coupon codes) above the fold
 * on mobile devices.
 *
 * Visual language:
 *   • Sale tile  → deep wine bg, ivory accents, ticker shimmer
 *   • Offers     → ivory bg, rosegold hairline, coupon-card metaphor
 * Both share the same composition (eyebrow → headline → CTA glyph).
 */
export default function SaleOffersTiles() {
    return (
        <section
            data-testid="home-sale-offers-tiles"
            className="mb-12 lg:mb-24 max-w-[90rem] mx-auto px-4 sm:px-6 lg:px-8"
        >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                {/* ── SALE TILE ─────────────────────────────────────────── */}
                <Link
                    to="/sale"
                    data-testid="home-tile-sale"
                    className="group relative overflow-hidden rounded-[28px] border border-wine/20 bg-wine text-ivory p-7 sm:p-9 lg:p-12 transition-transform hover:-translate-y-1 hover:shadow-2xl"
                >
                    {/* Decorative gradient flare */}
                    <span
                        aria-hidden
                        className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-rosegold/30 blur-3xl pointer-events-none"
                    />
                    {/* Diagonal stripe pattern */}
                    <span
                        aria-hidden
                        className="absolute inset-0 opacity-[0.04] pointer-events-none"
                        style={{
                            backgroundImage:
                                "repeating-linear-gradient(45deg, transparent 0 14px, white 14px 15px)",
                        }}
                    />
                    <div className="relative z-10 flex flex-col h-full min-h-[180px] sm:min-h-[220px]">
                        <p className="text-[10px] uppercase tracking-[0.32em] text-ivory/70 font-bold flex items-center gap-3 mb-3">
                            <Tag size={12} />
                            Edit · sale
                        </p>
                        <h3 className="font-display text-[1.75rem] sm:text-[2.25rem] lg:text-[2.65rem] leading-[1.05] tracking-tight font-light">
                            Pieces on{" "}
                            <span className="italic text-rosegold">sale</span>
                        </h3>
                        <p className="text-[13px] sm:text-sm text-ivory/80 mt-3 mb-auto max-w-[28ch] leading-relaxed">
                            Hand-picked styles at a softer price — limited inventory, while sizes last.
                        </p>
                        <div className="mt-6 flex items-center gap-3">
                            <span
                                className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-ivory text-wine text-[11px] font-bold uppercase tracking-[0.22em] transition-colors group-hover:bg-rosegold group-hover:text-ivory"
                                data-testid="home-tile-sale-cta"
                            >
                                Shop the sale
                                <ArrowUpRight size={14} />
                            </span>
                        </div>
                    </div>
                </Link>

                {/* ── OFFERS / COUPONS TILE ─────────────────────────────── */}
                <Link
                    to="/offers"
                    data-testid="home-tile-offers"
                    className="group relative overflow-hidden rounded-[28px] border border-rosegold/30 bg-ivory-soft p-7 sm:p-9 lg:p-12 transition-transform hover:-translate-y-1 hover:shadow-xl"
                >
                    {/* Coupon perforated-edge accent */}
                    <span
                        aria-hidden
                        className="absolute left-0 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-white border border-rosegold/30 -translate-x-1/2"
                    />
                    <span
                        aria-hidden
                        className="absolute right-0 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-white border border-rosegold/30 translate-x-1/2"
                    />
                    {/* Subtle rosegold glow */}
                    <span
                        aria-hidden
                        className="absolute -bottom-20 -left-20 w-72 h-72 rounded-full bg-rosegold/15 blur-3xl pointer-events-none"
                    />
                    <div className="relative z-10 flex flex-col h-full min-h-[180px] sm:min-h-[220px]">
                        <p className="text-[10px] uppercase tracking-[0.32em] text-rosegold font-bold flex items-center gap-3 mb-3">
                            <Ticket size={12} />
                            Your offers
                        </p>
                        <h3 className="font-display text-[1.75rem] sm:text-[2.25rem] lg:text-[2.65rem] leading-[1.05] tracking-tight font-light text-ink">
                            Live{" "}
                            <span className="italic text-wine">coupons</span>{" "}
                            &amp; codes
                        </h3>
                        <p className="text-[13px] sm:text-sm text-ink-soft mt-3 mb-auto max-w-[28ch] leading-relaxed">
                            Every code we&apos;re running right now — tap any to copy and apply at checkout.
                        </p>
                        <div className="mt-6 flex items-center gap-3">
                            <span
                                className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-ink text-ivory text-[11px] font-bold uppercase tracking-[0.22em] transition-colors group-hover:bg-rosegold"
                                data-testid="home-tile-offers-cta"
                            >
                                See all offers
                                <ArrowUpRight size={14} />
                            </span>
                        </div>
                    </div>
                </Link>
            </div>
        </section>
    );
}
